import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, ProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StorePdpState } from "@store/Store/Reducers/PdpReducer";
import some from "lodash/some";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    displayAsTable = "displayAsTable",
}

const mapStateToProps = (state: StorePdpState, props: HasProduct) => {
    const parameters = {
        expand: "images,badges",
        pageSize: 999,
        productId: props.product.id,
        relationship: "ExplodedViewProduct",
    };

    const { isLoading } = getDataView(state.data.products, parameters);

    return {
        state,
        products: getProductsForProductInfoList(state, "explodedViewItems"),
        isLoading,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    loadProductInfoList,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SubAssemblyItemsStyles {
    centeringWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}

export const specificationsStyles: SubAssemblyItemsStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const ExplodedViewNavigatorComponent = React.lazy(() => import("@store/Components/ExplodedView/ExplodedViewNavigator"));

const styles = specificationsStyles;

const ExplodedViewNavigator: React.FC<Props> = ({ product, products, isLoading, loadProductInfoList }) => {
    if (IS_SERVER_SIDE) {
        return null;
    }

    const productContext = React.useContext(ProductContext);

    const loadExplodedViewItems = (forceLoad: boolean) => {
        loadProductInfoList({
            id: "explodedViewItems",
            getProductCollectionParameter: {
                productId: productContext?.product?.id,
                relationship: "ExplodedViewProduct",
                pageSize: 999,
                expand: ["images", "badges"],
            },
            forceLoad,
        });
    };

    React.useEffect(() => {
        loadExplodedViewItems(true);
    }, [productContext?.product?.id]);

    React.useEffect(() => {
        // Products loaded in other sliders can lose their bubbleData because back-end is context dependent
        const noData = some(products, e => !e.properties.bubbleData);
        if (noData && !isLoading) {
            loadExplodedViewItems(true);
        }
    }, [products]);

    if (!products || !product) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {!IS_SERVER_SIDE && (
                <React.Suspense
                    fallback={
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner />
                        </StyledWrapper>
                    }
                >
                    <ExplodedViewNavigatorComponent product={product} />
                </React.Suspense>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ExplodedViewNavigator)),
    definition: {
        displayName: "Exploded View Navigator",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.displayAsTable,
                displayName: "Display As Table",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
